import * as THREE from 'three';
import { paintHull, paintPlanet, FACTION_PALETTES, FactionPalette } from './textures.js';

/**
 * Original Deepwake hulls, hand-authored from composed primitives.
 * Convention: nose points +Z (Destiny yaw 0 faces +Z). Dimensions are in
 * meters and roughly match each class's sim radius.
 *
 * Detail vocabulary: painted hull + dark trim + canopy glass material
 * layers, seeded greebles, turrets, antennas, pipes, and blinking nav
 * lights (collected into group.userData.blinkers for the render loop).
 */

interface Mats {
  hull: THREE.MeshStandardMaterial;
  trim: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  pal: FactionPalette;
}

function makeMats(classKey: string, faction: string): Mats {
  const maps = paintHull(classKey, faction);
  const pal = FACTION_PALETTES[faction] ?? FACTION_PALETTES.Waker;
  const hull = new THREE.MeshStandardMaterial({
    map: maps.map,
    emissiveMap: maps.emissiveMap,
    emissive: new THREE.Color(pal.glow),
    emissiveIntensity: 1.4,
    roughness: 0.62,
    metalness: 0.55,
  });
  const trim = new THREE.MeshStandardMaterial({
    color: new THREE.Color(pal.hullDark),
    roughness: 0.45,
    metalness: 0.85,
  });
  const glass = new THREE.MeshStandardMaterial({
    color: 0x0c1622,
    roughness: 0.15,
    metalness: 0.9,
    emissive: new THREE.Color(pal.glow),
    emissiveIntensity: 0.25,
  });
  return { hull, trim, glass, pal };
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------
// Part helpers
// ---------------------------------------------------------------------

function box(m: THREE.Material, w: number, h: number, d: number, x = 0, y = 0, z = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  return mesh;
}

function cyl(m: THREE.Material, r1: number, r2: number, len: number, x = 0, y = 0, z = 0, seg = 10): THREE.Mesh {
  const g = new THREE.CylinderGeometry(r1, r2, len, seg);
  g.rotateX(Math.PI / 2); // axis → Z
  const mesh = new THREE.Mesh(g, m);
  mesh.position.set(x, y, z);
  return mesh;
}

function engineGlow(pal: string, r: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.SphereGeometry(r, 10, 10),
    new THREE.MeshBasicMaterial({ color: pal, transparent: true, opacity: 0.9 }),
  );
  m.scale.z = 0.45;
  m.userData.engine = true;
  m.userData.baseR = r;
  return m;
}

/** Small blinking nav light; registered on the group for the render loop. */
function navLight(g: THREE.Group, color: number, r: number, x: number, y: number, z: number, phase = 0): void {
  const m = new THREE.Mesh(
    new THREE.SphereGeometry(r, 6, 6),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 }),
  );
  m.position.set(x, y, z);
  m.userData.blinkPhase = phase;
  g.add(m);
  (g.userData.blinkers ??= []).push(m);
}

/** Scatter small trim boxes over a box-ish region — instant surface detail. */
function greebles(
  g: THREE.Group,
  m: THREE.Material,
  seed: string,
  n: number,
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  scale = 1,
): void {
  const rand = mulberry32(hashStr(seed));
  for (let i = 0; i < n; i++) {
    const w = (0.6 + rand() * 1.8) * scale;
    const h = (0.3 + rand() * 0.9) * scale;
    const d = (0.6 + rand() * 2.6) * scale;
    g.add(
      box(
        m,
        w,
        h,
        d,
        cx + (rand() - 0.5) * sx,
        cy + (rand() - 0.5) * sy,
        cz + (rand() - 0.5) * sz,
      ),
    );
  }
}

function antenna(m: THREE.Material, len: number, x: number, y: number, z: number, tilt = 0): THREE.Group {
  const g = new THREE.Group();
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.12 * len * 0.08, 0.18 * len * 0.08, len, 5), m);
  mast.position.y = len / 2;
  const tip = new THREE.Mesh(new THREE.SphereGeometry(len * 0.03, 5, 5), m);
  tip.position.y = len;
  g.add(mast, tip);
  g.position.set(x, y, z);
  g.rotation.z = tilt;
  return g;
}

function turret(m: Mats, r: number, x: number, y: number, z: number): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(m.trim, r * 0.9, r, r * 0.8, 0, 0, 0, 8));
  const head = box(m.hull, r * 1.5, r * 0.8, r * 1.2, 0, 0, r * 0.6);
  const barrelA = cyl(m.trim, r * 0.14, r * 0.14, r * 2.4, -r * 0.35, 0, r * 1.8, 6);
  const barrelB = cyl(m.trim, r * 0.14, r * 0.14, r * 2.4, r * 0.35, 0, r * 1.8, 6);
  g.add(head, barrelA, barrelB);
  g.position.set(x, y, z);
  return g;
}

function dish(m: Mats, r: number, x: number, y: number, z: number, tiltX = -0.7): THREE.Group {
  const g = new THREE.Group();
  const bowl = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.45), m.trim);
  bowl.rotation.x = Math.PI;
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.08, r * 0.08, r * 0.8, 5), m.trim);
  stem.position.y = r * 0.5;
  g.add(bowl, stem);
  g.position.set(x, y, z);
  g.rotation.x = tiltX;
  return g;
}

/** Thin pipe run along Z. */
function pipe(m: THREE.Material, r: number, len: number, x: number, y: number, z: number): THREE.Mesh {
  return cyl(m, r, r, len, x, y, z, 6);
}

// ---------------------------------------------------------------------
// Ships
// ---------------------------------------------------------------------

type Builder = (g: THREE.Group, m: Mats) => void;

const BUILDERS: Record<string, Builder> = {
  // Skiff — mining drum: fat cylinder hull, ore maw front, twin side tanks
  skiff: (g, m) => {
    g.add(cyl(m.hull, 9, 11, 22, 0, 0, -2, 12));
    g.add(cyl(m.hull, 11.5, 8, 6, 0, 0, 11)); // maw collar
    g.add(cyl(m.trim, 7.5, 3.5, 5, 0, 0, 15)); // maw throat
    // Maw teeth ring
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const tooth = box(m.trim, 1.2, 1.2, 3.2, Math.cos(a) * 6.2, Math.sin(a) * 6.2, 15.5);
      tooth.rotation.z = a;
      g.add(tooth);
    }
    g.add(box(m.hull, 5, 5, 16, -12, 0, -4)); // side tanks
    g.add(box(m.hull, 5, 5, 16, 12, 0, -4));
    // Tank clamps
    for (const sx of [-12, 12]) for (const z of [-9, 1]) g.add(box(m.trim, 6, 6, 1.2, sx, 0, z));
    g.add(box(m.hull, 20, 1.6, 6, 0, 7, -8)); // spine bridge
    g.add(box(m.glass, 3.4, 1.8, 2.2, 0, 8.4, -6)); // conning glass
    // Crane arm
    const crane = new THREE.Group();
    crane.add(box(m.trim, 0.9, 6, 0.9, 0, 3, 0));
    crane.add(box(m.trim, 0.9, 0.9, 9, 0, 6, 3.5));
    crane.position.set(4, 7, -12);
    g.add(crane);
    // Drum pipes
    for (const a of [0.6, 2.2, 4.0]) {
      g.add(pipe(m.trim, 0.5, 20, Math.cos(a) * 10.2, Math.sin(a) * 10.2, -3));
    }
    greebles(g, m.trim, 'skiff', 14, 0, 0, -4, 16, 16, 18, 1.4);
    g.add(cyl(m.trim, 3.4, 4.2, 5, -6, -2, -15));
    g.add(cyl(m.trim, 3.4, 4.2, 5, 6, -2, -15));
    g.add(engineGlow(m.pal.glow, 3).translateX(-6).translateY(-2).translateZ(-18));
    g.add(engineGlow(m.pal.glow, 3).translateX(6).translateY(-2).translateZ(-18));
    navLight(g, 0xff5544, 0.7, -14.6, 0, -4, 0);
    navLight(g, 0x44ff77, 0.7, 14.6, 0, -4, 0.5);
    navLight(g, 0xffffff, 0.6, 0, 8, 3, 0.25);
  },

  // Dart — three engines, arrowhead frigate
  dart: (g, m) => {
    g.add(cyl(m.hull, 0.8, 3.4, 14, 0, 0, 6)); // nose
    g.add(box(m.hull, 7, 2.6, 10, 0, 0, -3)); // fuselage
    g.add(box(m.glass, 2.0, 1.1, 3.4, 0, 1.7, 1.5)); // canopy
    g.add(box(m.hull, 16, 1.2, 6, 0, 0, -5)); // wings
    // Wing trim edges + tip rails
    g.add(box(m.trim, 16.4, 0.4, 1.0, 0, 0, -2.6));
    for (const sx of [-8.2, 8.2]) g.add(box(m.trim, 0.8, 0.8, 5.4, sx, 0, -5));
    g.add(box(m.hull, 1.2, 6, 5, 0, 2, -6)); // fin
    g.add(box(m.trim, 0.5, 2.2, 2.2, 0, 5.2, -6.6)); // fin cap
    g.add(antenna(m.trim, 4.5, -1.6, 2.8, -8, 0.25));
    // Underslung gun pod
    g.add(box(m.trim, 2.6, 1.4, 5, 0, -1.9, 2));
    g.add(pipe(m.trim, 0.28, 6.5, -0.6, -2.1, 4.5));
    g.add(pipe(m.trim, 0.28, 6.5, 0.6, -2.1, 4.5));
    // Intake scoops
    for (const sx of [-3.1, 3.1]) g.add(box(m.trim, 1.4, 1.8, 4.2, sx, 0.6, -6.5));
    greebles(g, m.trim, 'dart', 8, 0, 0.8, -4, 5, 1.6, 8, 0.7);
    g.add(cyl(m.trim, 1.6, 2.2, 5, -6.5, 0, -8));
    g.add(cyl(m.trim, 1.6, 2.2, 5, 6.5, 0, -8));
    g.add(cyl(m.trim, 2.0, 2.8, 6, 0, -1, -9));
    g.add(engineGlow(m.pal.glow, 1.6).translateX(-6.5).translateZ(-11));
    g.add(engineGlow(m.pal.glow, 1.6).translateX(6.5).translateZ(-11));
    g.add(engineGlow(m.pal.glow, 2.1).translateY(-1).translateZ(-12.5));
    navLight(g, 0xff5544, 0.45, -8.4, 0, -5, 0);
    navLight(g, 0x44ff77, 0.45, 8.4, 0, -5, 0.5);
    navLight(g, 0xffffff, 0.4, 0, 5.6, -6.6, 0.75);
  },

  // Mule — boxes bolted to a reactor
  mule: (g, m) => {
    g.add(box(m.hull, 18, 14, 26, 0, 0, 2)); // cargo block
    // Individual container ribs across the block
    for (let i = 0; i < 4; i++) {
      g.add(box(m.trim, 18.6, 14.6, 0.9, 0, 0, -8 + i * 6.5));
    }
    g.add(box(m.hull, 16, 12, 8, 0, 0, 19)); // cab
    g.add(box(m.glass, 10, 3.2, 0.8, 0, 2.5, 23.2)); // windshield band
    g.add(cyl(m.trim, 6, 7, 12, 0, -2, -16, 12)); // reactor
    // Reactor cooling fins
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const fin = box(m.trim, 1.0, 3.4, 10, Math.cos(a) * 7.4, Math.sin(a) * 7.4 - 2, -16);
      fin.rotation.z = a;
      g.add(fin);
    }
    g.add(box(m.trim, 20, 2, 4, 0, 8.5, 2)); // roof rails
    g.add(box(m.hull, 2, 16, 22, -10.5, 0, 2)); // side frames
    g.add(box(m.hull, 2, 16, 22, 10.5, 0, 2));
    g.add(dish(m, 2.6, -6, 9.5, 16));
    g.add(antenna(m.trim, 7, 6, 8.5, 17, -0.15));
    g.add(box(m.trim, 3, 2, 3, 0, -8.2, 21)); // tow hook block
    greebles(g, m.trim, 'mule', 18, 0, 7.6, 2, 15, 1.4, 22, 1.6);
    g.add(engineGlow(m.pal.glow, 4).translateY(-2).translateZ(-23));
    navLight(g, 0xff5544, 0.8, -11.6, 6, 12, 0);
    navLight(g, 0x44ff77, 0.8, 11.6, 6, 12, 0.5);
    navLight(g, 0xffaa33, 0.7, 0, 9.6, -10, 0.25);
  },

  // Lance — one long spinal rail, cockpit as afterthought
  lance: (g, m) => {
    g.add(cyl(m.trim, 1.6, 1.6, 46, 0, 0, 2, 8)); // the rail
    // Charge coils along the rail
    for (let i = 0; i < 5; i++) {
      const coil = new THREE.Mesh(new THREE.TorusGeometry(2.6, 0.55, 6, 12), m.hull.clone());
      coil.position.set(0, 0, 14 - i * 5.5);
      g.add(coil);
    }
    g.add(cyl(m.trim, 2.6, 1.2, 8, 0, 0, 26)); // muzzle
    // Muzzle brake slots
    for (const a of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
      g.add(box(m.trim, 0.7, 0.7, 4.4, Math.cos(a) * 2.9, Math.sin(a) * 2.9, 27));
    }
    g.add(box(m.hull, 6, 5, 14, 0, -1, -8)); // engineering hull
    g.add(box(m.glass, 2.6, 1.6, 3.4, 0, 3.0, -13.6)); // cockpit glass
    g.add(box(m.hull, 3.4, 3, 5, 0, 2.6, -14)); // cockpit frame
    g.add(box(m.hull, 1.4, 9, 8, 0, 0, -2)); // dorsal blade
    // Heat fin array on the blade
    for (let i = 0; i < 4; i++) g.add(box(m.trim, 0.4, 7 - i * 1.2, 0.8, 0, 0.6, 0.6 - i * 1.6));
    g.add(box(m.hull, 12, 1.4, 8, 0, -2.4, -4)); // outriggers
    g.add(turret(m, 1.1, -4.6, -1.4, 0));
    g.add(turret(m, 1.1, 4.6, -1.4, 0));
    g.add(antenna(m.trim, 6, 0, 4.4, -9, 0.3));
    greebles(g, m.trim, 'lance', 10, 0, -1, -7, 5, 4, 12, 0.8);
    g.add(cyl(m.trim, 1.8, 2.4, 6, -5, -2.4, -10));
    g.add(cyl(m.trim, 1.8, 2.4, 6, 5, -2.4, -10));
    g.add(engineGlow(m.pal.glow, 1.8).translateX(-5).translateY(-2.4).translateZ(-13.5));
    g.add(engineGlow(m.pal.glow, 1.8).translateX(5).translateY(-2.4).translateZ(-13.5));
    navLight(g, 0xff5544, 0.5, -6.4, -2.4, -4, 0);
    navLight(g, 0x44ff77, 0.5, 6.4, -2.4, -4, 0.5);
    navLight(g, 0xffffff, 0.5, 0, 4.6, 24, 0.25); // muzzle warning light
  },

  // Reliquary — cruiser grown around a Warden gate fragment
  reliquary: (g, m) => {
    g.add(cyl(m.hull, 9, 13, 46, 0, 0, 0, 7)); // faceted main hull
    // Hull ribs
    for (let i = 0; i < 5; i++) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(11.5 + (i % 2), 0.7, 6, 7), m.trim);
      rib.position.z = 16 - i * 8;
      g.add(rib);
    }
    g.add(box(m.hull, 10, 8, 18, 0, 6, 12)); // conning castle
    g.add(box(m.glass, 6.5, 2.0, 1.0, 0, 8.4, 20.6)); // bridge glass
    // The fragment: a tilted obsidian ring embedded amidships
    const frag = new THREE.MeshStandardMaterial({ color: 0x14161c, roughness: 0.3, metalness: 0.9 });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(15, 2.2, 8, 28), frag);
    ring.position.set(0, 0, -4);
    ring.rotation.y = Math.PI / 2;
    ring.rotation.x = 0.35;
    g.add(ring);
    // Fragment glow seams
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const seam = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 1.2, 3.4),
        new THREE.MeshBasicMaterial({ color: 0x86f0ff, transparent: true, opacity: 0.8 }),
      );
      seam.position.set(Math.sin(a) * 0.35 * 15, Math.cos(a) * 15, -4 + Math.sin(a) * -0.0);
      seam.position.x += Math.sin(0.35) * 0; // stays near ring plane; approximation is fine at this scale
      seam.userData.blinkPhase = i / 6;
      g.add(seam);
      (g.userData.blinkers ??= []).push(seam);
    }
    g.add(box(m.hull, 26, 2.2, 14, 0, -6, 6)); // ventral vanes
    g.add(box(m.trim, 27, 0.8, 3, 0, -6, 1)); // vane spar
    g.add(turret(m, 1.6, -8, 7.2, 4));
    g.add(turret(m, 1.6, 8, 7.2, 4));
    g.add(dish(m, 3.2, 0, 10.6, 8));
    g.add(antenna(m.trim, 9, -3, 9.8, 16, 0.2));
    greebles(g, m.trim, 'reliquary', 20, 0, 4, 6, 9, 4, 30, 1.5);
    g.add(cyl(m.trim, 4, 5.4, 10, -8, 0, -22));
    g.add(cyl(m.trim, 4, 5.4, 10, 8, 0, -22));
    g.add(engineGlow(m.pal.glow, 4).translateX(-8).translateZ(-28));
    g.add(engineGlow(m.pal.glow, 4).translateX(8).translateZ(-28));
    navLight(g, 0xff5544, 0.9, -13.5, 0, 8, 0);
    navLight(g, 0x44ff77, 0.9, 13.5, 0, 8, 0.5);
    navLight(g, 0xffffff, 0.8, 0, 10.4, 12, 0.25);
  },

  // Bastion — mobile foundry line
  bastion: (g, m) => {
    g.add(box(m.hull, 30, 20, 60, 0, 0, 0)); // main works
    g.add(box(m.trim, 34, 4, 20, 0, 12, -6)); // gantries
    // Gantry cranes
    for (const sx of [-14, 14]) {
      g.add(box(m.trim, 1.4, 8, 1.4, sx, 17, -6));
      g.add(box(m.trim, 10, 1.2, 1.2, sx > 0 ? sx - 5 : sx + 5, 20.6, -6));
    }
    g.add(cyl(m.trim, 4, 4, 24, -10, 14, 0, 8)); // stacks
    g.add(cyl(m.trim, 4, 4, 24, 10, 14, 0, 8));
    // Stack tip glows
    for (const sx of [-10, 10]) {
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(2.4, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xffa04a, transparent: true, opacity: 0.85 }),
      );
      tip.position.set(sx, 14, 13);
      tip.userData.blinkPhase = sx > 0 ? 0.5 : 0;
      g.add(tip);
      (g.userData.blinkers ??= []).push(tip);
    }
    g.add(box(m.hull, 22, 14, 14, 0, -2, 37)); // refinery maw
    g.add(box(m.trim, 23, 15, 1.4, 0, -2, 30)); // maw collar
    // Container rows on deck
    const rand = mulberry32(hashStr('bastion-cargo'));
    for (let i = 0; i < 8; i++) {
      g.add(
        box(
          m.trim,
          3.4,
          2.6,
          6,
          -12 + (i % 4) * 8 + rand() * 1.5,
          11.4,
          10 + Math.floor(i / 4) * 8,
        ),
      );
    }
    // Catwalk rails
    g.add(box(m.trim, 30.8, 0.5, 0.5, 0, 10.4, 22));
    g.add(box(m.trim, 30.8, 0.5, 0.5, 0, 10.4, -26));
    g.add(dish(m, 4.2, 12, 13, -20));
    g.add(antenna(m.trim, 12, -13, 10, -24, 0.18));
    g.add(turret(m, 2.0, -12, -11, 24));
    g.add(turret(m, 2.0, 12, -11, 24));
    greebles(g, m.trim, 'bastion', 26, 0, 0, 0, 28, 18, 55, 2.4);
    g.add(cyl(m.trim, 9, 11, 16, -14, -6, -30, 10));
    g.add(cyl(m.trim, 9, 11, 16, 14, -6, -30, 10));
    g.add(engineGlow(m.pal.glow, 7).translateX(-14).translateY(-6).translateZ(-40));
    g.add(engineGlow(m.pal.glow, 7).translateX(14).translateY(-6).translateZ(-40));
    navLight(g, 0xff5544, 1.4, -16, 0, 20, 0);
    navLight(g, 0x44ff77, 1.4, 16, 0, 20, 0.5);
    navLight(g, 0xffffff, 1.1, 0, 21.4, -6, 0.25);
  },
};

export function buildShip(classKey: string, faction: string): THREE.Group {
  const group = new THREE.Group();
  const m = makeMats(classKey, faction);
  (BUILDERS[classKey] ?? BUILDERS.dart)(group, m);
  return group;
}

// ---------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------

/** Stations: one silhouette per faction, now with lit sprawl. */
export function buildStation(faction: string): THREE.Group {
  const g = new THREE.Group();
  const m = makeMats('station', faction);
  const pal = m.pal;

  const addNav = (x: number, y: number, z: number, phase: number, color = 0xffffff) =>
    navLight(g, color, 26, x, y, z, phase);

  if (faction === 'Tidemark Compact') {
    // Compact anchorage: stacked spheres on a mast, trade booms
    g.add(new THREE.Mesh(new THREE.SphereGeometry(620, 16, 14), m.hull));
    const upper = new THREE.Mesh(new THREE.SphereGeometry(420, 14, 12), m.hull);
    upper.position.y = 850;
    const lower = new THREE.Mesh(new THREE.SphereGeometry(420, 14, 12), m.hull);
    lower.position.y = -850;
    g.add(upper, lower);
    g.add(box(m.trim, 80, 2600, 80));
    g.add(box(m.trim, 2800, 60, 60, 0, 300, 0));
    g.add(box(m.trim, 60, 60, 2800, 0, -300, 0));
    // Moored cargo pods along the booms
    const rand = mulberry32(1);
    for (let i = 0; i < 6; i++) {
      g.add(box(m.trim, 110, 90, 190, -1200 + i * 480 + rand() * 60, 300 + (i % 2 ? 120 : -120), 0));
    }
    g.add(dish(m, 180, 0, 1350, 0, -0.4));
    greebles(g, m.trim, 'stn-tidemark', 22, 0, 0, 0, 900, 900, 900, 55);
    addNav(1450, 300, 0, 0, 0xff5544);
    addNav(-1450, 300, 0, 0.5, 0x44ff77);
    addNav(0, 1500, 0, 0.25);
    addNav(0, -300, 1450, 0.75);
  } else if (faction === 'The Vigil') {
    // Vigil spire: cathedral needle with a gate-shard crown
    const spire = new THREE.Mesh(new THREE.ConeGeometry(520, 2600, 7), m.hull);
    spire.rotation.x = Math.PI;
    g.add(spire);
    const base = cyl(m.hull, 680, 780, 500, 0, -1500, 0, 7);
    base.rotation.x = Math.PI / 2;
    g.add(base);
    const crown = new THREE.Mesh(new THREE.TorusGeometry(700, 90, 8, 7), m.trim);
    crown.position.y = 1500;
    crown.rotation.x = Math.PI / 2;
    g.add(crown);
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2;
      const fin = box(m.trim, 60, 900, 200, Math.cos(a) * 760, -1100, Math.sin(a) * 760);
      fin.rotation.y = -a;
      g.add(fin);
      // Votive lights along the crown
      const votive = new THREE.Mesh(
        new THREE.SphereGeometry(30, 6, 6),
        new THREE.MeshBasicMaterial({ color: 0xc9a7ff, transparent: true, opacity: 0.9 }),
      );
      votive.position.set(Math.cos(a) * 700, 1500, Math.sin(a) * 700);
      votive.userData.blinkPhase = i / 7;
      g.add(votive);
      (g.userData.blinkers ??= []).push(votive);
    }
    // Bell: a dark shard suspended in the crown
    const bell = new THREE.Mesh(
      new THREE.OctahedronGeometry(160, 0),
      new THREE.MeshStandardMaterial({ color: 0x14161c, roughness: 0.25, metalness: 0.95 }),
    );
    bell.position.y = 1500;
    bell.userData.spin = 0.0004;
    g.add(bell);
    greebles(g, m.trim, 'stn-vigil', 16, 0, -900, 0, 800, 900, 800, 40);
    addNav(0, 1750, 0, 0);
    addNav(0, -1800, 0, 0.5, 0xff5544);
  } else if (faction === 'Unbound') {
    // Unbound harbor: welded asymmetric junk cluster
    g.add(box(m.hull, 900, 500, 1300, 0, 0, 0));
    g.add(box(m.hull, 500, 900, 600, 600, 200, -300));
    g.add(cyl(m.trim, 260, 260, 1500, -500, -150, 200, 8));
    g.add(box(m.trim, 1600, 120, 120, 100, 500, 300));
    const bowl = new THREE.Mesh(new THREE.SphereGeometry(380, 10, 8, 0, Math.PI), m.trim);
    bowl.position.set(-300, 550, -400);
    bowl.rotation.x = -0.9;
    g.add(bowl);
    // Welded-on shanty pods + scav floodlights
    const rand = mulberry32(7);
    for (let i = 0; i < 9; i++) {
      g.add(
        box(
          m.trim,
          120 + rand() * 160,
          90 + rand() * 120,
          140 + rand() * 200,
          (rand() - 0.5) * 1000,
          (rand() - 0.5) * 600,
          (rand() - 0.5) * 1200,
        ),
      );
    }
    // Hulk being broken: half a mule welded to the side
    g.add(box(m.hull, 200, 160, 300, -700, 300, 500));
    g.add(box(m.trim, 40, 400, 40, -700, 100, 650));
    greebles(g, m.trim, 'stn-unbound', 30, 0, 0, 0, 1400, 900, 1500, 60);
    addNav(850, 500, 300, 0, 0xffaa33);
    addNav(-780, -150, 200, 0.33, 0xffaa33);
    addNav(100, 560, 1000, 0.66, 0xff5544);
  } else {
    // Combine foundry: ringed drum with docking maw
    g.add(cyl(m.hull, 700, 700, 1400, 0, 0, 0, 18));
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1250, 130, 10, 36), m.hull);
    ring.userData.spin = 0.00018; // the ring turns, foundry-line style
    g.add(ring);
    g.add(box(m.trim, 2500, 90, 90));
    const spokeB = box(m.trim, 90, 90, 2500);
    spokeB.rotation.x = Math.PI / 2;
    g.add(spokeB);
    g.add(cyl(m.trim, 420, 300, 500, 0, 0, 900, 12));
    // Docking maw approach ring glow
    const mawGlow = new THREE.Mesh(
      new THREE.TorusGeometry(360, 14, 6, 24),
      new THREE.MeshBasicMaterial({ color: pal.glow, transparent: true, opacity: 0.7 }),
    );
    mawGlow.position.z = 1150;
    g.add(mawGlow);
    (g.userData.blinkers ??= []).push(mawGlow);
    // Industrial sprawl on the drum
    g.add(dish(m, 170, 400, 760, -300, -0.5));
    g.add(antenna(m.trim, 700, -350, 700, 200, 0.12));
    greebles(g, m.trim, 'stn-combine', 26, 0, 0, 0, 1200, 1200, 1300, 50);
    addNav(1380, 0, 0, 0, 0xff5544);
    addNav(-1380, 0, 0, 0.5, 0x44ff77);
    addNav(0, 0, 1420, 0.25);
    addNav(0, 780, 0, 0.75);
  }

  const beacon = new THREE.Mesh(
    new THREE.SphereGeometry(60, 8, 8),
    new THREE.MeshBasicMaterial({ color: pal.glow, transparent: true, opacity: 0.9 }),
  );
  beacon.position.set(0, 0, 1200);
  beacon.userData.blinker = true;
  g.add(beacon);
  (g.userData.blinkers ??= []).push(beacon);
  return g;
}

// ---------------------------------------------------------------------
// Celestials & structures
// ---------------------------------------------------------------------

/** Planet with painted texture and a soft atmosphere rim. */
export function buildPlanet(kind: 'gas' | 'rock' | 'ice', seed: number, radius: number): THREE.Group {
  const g = new THREE.Group();
  const tex = paintPlanet(kind, seed);
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 36, 28),
    new THREE.MeshStandardMaterial({ map: tex, roughness: 1, metalness: 0 }),
  );
  body.rotation.z = (seed % 100) / 100 - 0.5;
  body.userData.spin = kind === 'gas' ? 0.00012 : 0.00005;
  const atmoColor = kind === 'gas' ? 0xd9b98a : kind === 'ice' ? 0xaee2ff : 0xc9a289;
  const atmo = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 1.03, 36, 28),
    new THREE.MeshBasicMaterial({ color: atmoColor, transparent: true, opacity: 0.12, side: THREE.BackSide }),
  );
  g.add(body, atmo);
  // Gas giants get a thin painted ring now and then
  if (kind === 'gas' && seed % 3 === 0) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(radius * 1.45, radius * 2.1, 48),
      new THREE.MeshBasicMaterial({ color: 0xbfae8f, transparent: true, opacity: 0.28, side: THREE.DoubleSide }),
    );
    ring.rotation.x = Math.PI / 2 - 0.35;
    g.add(ring);
  }
  return g;
}

/** Warden gate: segmented obsidian torus; runes, drifting shards, shimmer. */
export function buildGate(): THREE.Group {
  const g = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x14161c, roughness: 0.3, metalness: 0.9 });
  const shimmer = new THREE.MeshBasicMaterial({ color: 0x86f0ff, transparent: true, opacity: 0.16, side: THREE.DoubleSide });

  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const seg = new THREE.Mesh(new THREE.BoxGeometry(220, 620, 320), dark);
    seg.position.set(Math.cos(a) * 1500, Math.sin(a) * 1500, 0);
    seg.rotation.z = a + Math.PI / 2;
    g.add(seg);
    // Rune lights on each segment face, waking in sequence
    const rune = new THREE.Mesh(
      new THREE.BoxGeometry(30, 210, 8),
      new THREE.MeshBasicMaterial({ color: 0x86f0ff, transparent: true, opacity: 0.85 }),
    );
    rune.position.set(Math.cos(a) * 1500, Math.sin(a) * 1500, 168);
    rune.rotation.z = a + Math.PI / 2;
    rune.userData.blinkPhase = i / 9;
    g.add(rune);
    (g.userData.blinkers ??= []).push(rune);
  }
  const disc = new THREE.Mesh(new THREE.CircleGeometry(1350, 40), shimmer);
  disc.userData.gateDisc = true;
  g.add(disc);
  // Sheared shard fragments drifting near the ring
  for (let i = 0; i < 4; i++) {
    const shard = new THREE.Mesh(new THREE.TetrahedronGeometry(90 + i * 30, 0), dark);
    const a = i * 1.7;
    shard.position.set(Math.cos(a) * 1950, Math.sin(a) * 1900, (i - 1.5) * 260);
    shard.rotation.set(i, i * 2.1, i * 0.6);
    shard.userData.spin = 0.0009 + i * 0.0004;
    g.add(shard);
  }
  return g;
}

/** Belt beacon: tumbling shard cluster with a scaffold mast light. */
export function buildBeltBeacon(): THREE.Group {
  const g = new THREE.Group();
  const rock = new THREE.MeshStandardMaterial({ color: 0x77706a, roughness: 0.95, metalness: 0.05 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x3c4046, roughness: 0.5, metalness: 0.8 });
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const s = 90 + (i % 3) * 70;
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 0), rock);
    m.position.set(Math.cos(a) * 420, ((i % 3) - 1) * 160, Math.sin(a) * 420);
    m.rotation.set(i, i * 2.3, i * 0.7);
    g.add(m);
  }
  // Prospector's claim mast
  g.add(box(trim, 18, 420, 18, 0, 210, 0));
  g.add(box(trim, 130, 14, 14, 0, 380, 0));
  navLight(g, 0xffaa33, 26, 0, 440, 0, 0);
  return g;
}

/** Wreck: charred hull plates with a dying ember. */
export function buildWreck(): THREE.Group {
  const g = new THREE.Group();
  const charred = new THREE.MeshStandardMaterial({ color: 0x2c2622, roughness: 0.8, metalness: 0.6 });
  for (let i = 0; i < 5; i++) {
    const p = new THREE.Mesh(new THREE.BoxGeometry(8 + i * 4, 3, 14 - i * 2), charred);
    p.position.set((i - 2) * 6, (i % 2) * 4 - 2, i * 3 - 6);
    p.rotation.set(i * 0.7, i * 1.3, i * 0.4);
    g.add(p);
  }
  // Snapped spar + tangled pipe
  const spar = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 22, 6), charred);
  spar.rotation.z = 0.9;
  spar.position.set(2, 3, 0);
  g.add(spar);
  const ember = new THREE.Mesh(
    new THREE.SphereGeometry(1.6, 6, 6),
    new THREE.MeshBasicMaterial({ color: 0xff7a3a, transparent: true, opacity: 0.9 }),
  );
  ember.userData.blinkPhase = 0.13;
  g.add(ember);
  (g.userData.blinkers ??= []).push(ember);
  return g;
}

/** Asteroid: a clump of 2-4 fused rocks, tinted by id. */
export function buildAsteroid(id: number, radius: number): THREE.Group {
  const g = new THREE.Group();
  const rand = mulberry32(id >>> 0);
  const tone = 0.85 + rand() * 0.35;
  const rock = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.54 * tone, 0.5 * tone, 0.45 * tone),
    roughness: 0.95,
    metalness: 0.05,
    flatShading: true,
  });
  const r = Math.max(20, radius);
  const n = 2 + Math.floor(rand() * 3);
  for (let i = 0; i < n; i++) {
    const s = r * (i === 0 ? 1 : 0.35 + rand() * 0.45);
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 1), rock);
    if (i > 0) {
      m.position.set((rand() - 0.5) * r * 1.4, (rand() - 0.5) * r * 1.0, (rand() - 0.5) * r * 1.4);
    }
    m.rotation.set(rand() * 6, rand() * 6, rand() * 6);
    m.scale.set(1, 0.75 + rand() * 0.5, 0.8 + rand() * 0.4);
    g.add(m);
  }
  // Ore glint flecks
  const glint = new THREE.Mesh(
    new THREE.IcosahedronGeometry(r * 0.16, 0),
    new THREE.MeshStandardMaterial({ color: 0x9fd4ff, roughness: 0.2, metalness: 0.9, emissive: 0x2a4a5a }),
  );
  glint.position.set(r * 0.7, r * 0.3, 0);
  g.add(glint);
  return g;
}

export function buildSun(radius: number): THREE.Group {
  const g = new THREE.Group();
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0xfff2cf }),
  );
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 1.6, 24, 24),
    new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0.18, side: THREE.BackSide }),
  );
  g.add(core, halo);
  // Corona sprite: radial gradient billboard, always camera-facing
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d')!;
  const grd = ctx.createRadialGradient(128, 128, 24, 128, 128, 128);
  grd.addColorStop(0, 'rgba(255,240,205,0.9)');
  grd.addColorStop(0.35, 'rgba(255,205,120,0.35)');
  grd.addColorStop(1, 'rgba(255,180,90,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, 256, 256);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(c), transparent: true, depthWrite: false }),
  );
  sprite.scale.setScalar(radius * 5.2);
  g.add(sprite);
  return g;
}
